import type { ReactNode } from 'react';
import { enableIterativeDiffRendering } from '@gonfalon/dogfood-flags/dogfood-flags';
import classNames from 'clsx';
import type { Change } from 'diff';

import { buildCollapsibleDiffPlan, type ChangeGroup, expandUnchangedLinesLabel } from './diffCollapse';
import { diffElementClassNames } from './diffElementClassNames';
import { buildAddedLineWordDiffSpans, buildRemovedLineWordDiffSpans } from './diffWordSpans';
import type { ElementState } from './types';
import { elementDataAttributes } from './utils';

import styles from './Diff.module.css';

/**
 * `enable-iterative-diff-rendering` selects between the iterative walk and the
 * legacy recursive walk, which overflows the JS stack on large diffs.
 */
export function renderSplitDiff(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  leftElements: React.ReactNode[] = [],
  rightElements: React.ReactNode[] = [],
  lineNumber: number = 1,
  prevItem: Change | null = null,
): { leftElements: React.ReactNode[]; rightElements: React.ReactNode[] } {
  return enableIterativeDiffRendering()
    ? renderSplitDiffIteratively(
        diff,
        showLineNumbers,
        includeSubDiffs,
        leftElements,
        rightElements,
        lineNumber,
        prevItem,
      )
    : renderSplitDiffRecursively(
        diff,
        showLineNumbers,
        includeSubDiffs,
        leftElements,
        rightElements,
        lineNumber,
        prevItem,
      );
}

function renderSplitDiffIteratively(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  leftElements: React.ReactNode[],
  rightElements: React.ReactNode[],
  lineNumber: number,
  prevItem: Change | null,
): { leftElements: React.ReactNode[]; rightElements: React.ReactNode[] } {
  let currentLineNumber = lineNumber;
  let previous = prevItem;

  for (let i = 0; i < diff.length; i += 1) {
    const current = diff[i];
    const next = diff[i + 1];
    const elementState: ElementState = current.added ? 'added' : current.removed ? 'removed' : 'unchanged';

    let leftInnerElements: ReactNode[] = [];
    let rightInnerElements: ReactNode[] = [];

    if (current.added) {
      rightInnerElements = [current.value];
      if (previous?.removed && includeSubDiffs) {
        rightInnerElements = buildAddedLineWordDiffSpans(previous.value, current.value, currentLineNumber);
      }
      leftInnerElements = [<br key={`empty-left-${currentLineNumber}`} />];
    } else if (current.removed) {
      leftInnerElements = [current.value];
      if (next?.added && includeSubDiffs) {
        leftInnerElements = buildRemovedLineWordDiffSpans(current.value, next.value, currentLineNumber);
      }
      rightInnerElements = [<br key={`empty-right-${currentLineNumber}`} />];
    } else {
      leftInnerElements = [current.value];
      rightInnerElements = [current.value];
    }

    if (current.value.trim() === '') {
      const emptyLine = <br key={`empty-${currentLineNumber}`} />;
      leftInnerElements = [emptyLine];
      rightInnerElements = [emptyLine];
    }

    const elementClassName = diffElementClassNames[elementState];
    const dataAttribute = elementDataAttributes[elementState];

    if (current.removed || !current.added) {
      leftElements.push(
        <div
          key={`left-${elementState}-${currentLineNumber}-${current.value.length}`}
          className={classNames(elementClassName, { [styles.showLineNumbers]: showLineNumbers })}
          {...{ [`data-${dataAttribute}`]: true }}
        >
          {showLineNumbers && (
            <div data-line-number className={styles.lineNumber}>
              {currentLineNumber}
            </div>
          )}
          {leftInnerElements}
        </div>,
      );
    }

    if (current.added || !current.removed) {
      rightElements.push(
        <div
          key={`right-${elementState}-${currentLineNumber}-${current.value.length}`}
          className={classNames(elementClassName, { [styles.showLineNumbers]: showLineNumbers })}
          {...{ [`data-${dataAttribute}`]: true }}
        >
          {showLineNumbers && (
            <div data-line-number className={styles.lineNumber}>
              {currentLineNumber}
            </div>
          )}
          {rightInnerElements}
        </div>,
      );
    }

    if (next) {
      currentLineNumber = current.removed && next.added ? currentLineNumber : currentLineNumber + 1;
      previous = current;
    }
  }

  return { leftElements, rightElements };
}

function renderSplitDiffRecursively(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  leftElements: React.ReactNode[],
  rightElements: React.ReactNode[],
  lineNumber: number,
  prevItem: Change | null,
): { leftElements: React.ReactNode[]; rightElements: React.ReactNode[] } {
  if (diff.length === 0) {
    return { leftElements, rightElements };
  }

  const [current, ...rest] = diff;
  const elementState: ElementState = current.added ? 'added' : current.removed ? 'removed' : 'unchanged';
  const next = rest[0];

  let leftInnerElements: ReactNode[] = [];
  let rightInnerElements: ReactNode[] = [];

  if (current.added) {
    rightInnerElements = [current.value];
    if (prevItem?.removed && includeSubDiffs) {
      rightInnerElements = buildAddedLineWordDiffSpans(prevItem.value, current.value, lineNumber);
    }
    leftInnerElements = [<br key={`empty-left-${lineNumber}`} />];
  } else if (current.removed) {
    leftInnerElements = [current.value];
    if (next?.added && includeSubDiffs) {
      leftInnerElements = buildRemovedLineWordDiffSpans(current.value, next.value, lineNumber);
    }
    rightInnerElements = [<br key={`empty-right-${lineNumber}`} />];
  } else {
    leftInnerElements = [current.value];
    rightInnerElements = [current.value];
  }

  if (current.value.trim() === '') {
    const emptyLine = <br key={`empty-${lineNumber}`} />;
    leftInnerElements = [emptyLine];
    rightInnerElements = [emptyLine];
  }

  const elementClassName = diffElementClassNames[elementState];
  const dataAttribute = elementDataAttributes[elementState];

  const leftCopy = [...leftElements];
  const rightCopy = [...rightElements];

  if (current.removed || !current.added) {
    leftCopy.push(
      <div
        key={`left-${elementState}-${lineNumber}-${current.value.length}`}
        className={classNames(elementClassName, { [styles.showLineNumbers]: showLineNumbers })}
        {...{ [`data-${dataAttribute}`]: true }}
      >
        {showLineNumbers && (
          <div data-line-number className={styles.lineNumber}>
            {lineNumber}
          </div>
        )}
        {leftInnerElements}
      </div>,
    );
  }

  if (current.added || !current.removed) {
    rightCopy.push(
      <div
        key={`right-${elementState}-${lineNumber}-${current.value.length}`}
        className={classNames(elementClassName, { [styles.showLineNumbers]: showLineNumbers })}
        {...{ [`data-${dataAttribute}`]: true }}
      >
        {showLineNumbers && (
          <div data-line-number className={styles.lineNumber}>
            {lineNumber}
          </div>
        )}
        {rightInnerElements}
      </div>,
    );
  }

  if (!next) {
    return { leftElements: leftCopy, rightElements: rightCopy };
  }

  const nextLineNumber = current.removed && next.added ? lineNumber : lineNumber + 1;

  return renderSplitDiffRecursively(
    rest,
    showLineNumbers,
    includeSubDiffs,
    leftCopy,
    rightCopy,
    nextLineNumber,
    current,
  );
}

export function renderCollapsibleSplitDiff(
  groups: ChangeGroup[],
  expandedGroups: Set<number>,
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  contextLines: number,
  onToggleExpand: (groupIndex: number) => void,
): { leftElements: ReactNode[]; rightElements: ReactNode[] } {
  const leftElements: ReactNode[] = [];
  const rightElements: ReactNode[] = [];

  const pushSplitResult = (changes: Change[], startLineNumber: number) => {
    const { leftElements: l, rightElements: r } = renderSplitDiff(
      changes,
      showLineNumbers,
      includeSubDiffs,
      [],
      [],
      startLineNumber,
    );
    leftElements.push(...l);
    rightElements.push(...r);
  };

  for (const step of buildCollapsibleDiffPlan(groups, expandedGroups, contextLines)) {
    if (step.kind === 'render') {
      pushSplitResult(step.changes, step.startLine);
    } else {
      const label = expandUnchangedLinesLabel(step.hiddenCount, step.position);
      const makeBtn = (key: string) => (
        <button
          key={key}
          className={styles.expandSection}
          onClick={() => onToggleExpand(step.groupIndex)}
          type="button"
          aria-label={label}
        >
          <div className={styles.expandButton} data-testid="expand-unchanged-lines">
            <span className={styles.expandIcon}>⋯</span>
            <span className={styles.expandText}>{label}</span>
          </div>
        </button>
      );
      leftElements.push(makeBtn(`expand-left-${step.groupIndex}`));
      rightElements.push(makeBtn(`expand-right-${step.groupIndex}`));
    }
  }

  return { leftElements, rightElements };
}
